/* eslint-disable @typescript-eslint/no-misused-promises */
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, TextField, MenuItem } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import character from '../../assets/images/intake/step2/char.svg';
import { useUserAsaTaskHistoryContext } from '../../hooks/use-user-asa-task-history-context.hooks';
import { useAuthContext } from '../../hooks/use-user-auth-context.hooks';
import {
  AsaTaskName,
  AsaUserTasks,
  completeASATask,
  dispatchUpdateAsaTaskHistoryInfo,
} from '../../redux/slice/asa-task-history';
import { CustomUser } from '../../redux/slice/auth/CustomUser';
import {
  CareerRoadMapDto,
  LocationDto,
} from '../../redux/slice/career-roadmap/career-roadmap-type';
import { dispatchCareerRoadMapAssessment } from '../../redux/slice/career-roadmap/career-roadmap.operations';
import { roadMapQuestions } from '../../redux/slice/career-roadmap/careerRoadMapQuestions';
import { State as StateValue } from '../../redux/slice/form-intake/form-intake.types';
import { RootState, useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';
// import { EnvironmentVariable } from '../../utils/constants';
import { EnvironmentVariable } from '../../utils/constants';
import { buildASANextTaskUrl } from '../../utils/helper';
import { CircularLoader } from '../loader/CircularLoader';

interface ComponentProps {
  step: number;
  onBack: () => void;
}

interface FormData extends LocationDto {}

const CareerRoadMapSchema = yup
  .object({
    state: yup.string().required('Please select or enter a state'),
    commute: yup.string().required('Please select commute preference'),
  })
  .required();

export const Location: React.FC<ComponentProps> = ({ step, onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // asa task
  const currentUser: CustomUser | null = useAuthContext();
  const [currsentAsaTask, setCurrentAsaTask] = useState<AsaUserTasks | undefined>(undefined);
  const asaUserTaskHistory = useUserAsaTaskHistoryContext();
  useEffect(() => {
    if (
      currentUser?.claims?.isAsaUser &&
      Object.keys(asaUserTaskHistory).length &&
      asaUserTaskHistory?.tasks?.length
    ) {
      const currentTask = asaUserTaskHistory.tasks?.find(
        (x) => !x.isCompleted && x.taskName === AsaTaskName.CAREERROADMAP,
      );

      const doseCareerRoadMapTaskExists = asaUserTaskHistory.tasks?.find(
        (x) => x.taskName === AsaTaskName.CAREERROADMAP,
      );

      const signUpTask = asaUserTaskHistory.tasks?.find(
        (x) => x.isCompleted && x.taskName === AsaTaskName.SIGNUP,
      );
      if (!currentTask && !doseCareerRoadMapTaskExists && signUpTask) {
        const asaParams: string | null = sessionStorage.getItem('asa_params');

        const asaParamObj = asaParams && JSON.parse(asaParams);

        void dispatch(
          dispatchUpdateAsaTaskHistoryInfo({
            asaUserId: asaParamObj['user_id'] as number,
            taskId: asaParamObj['unique_task_key'],
            taskStep: asaParamObj['step_number'],
            taskUrl: asaParamObj['task_url'],
            taskName: AsaTaskName.CAREERROADMAP,
            totalSteps: 17,
            isCompleted: false,
          }),
        );
      } else {
        setCurrentAsaTask(currentTask);
      }

      if (currentTask && !currentTask?.startDate) {
        void dispatch(
          dispatchUpdateAsaTaskHistoryInfo({
            asaUserId: currentUser?.claims?.asaUserId || 0,
            taskId: currentTask?.taskId || '',
            taskStep: currentTask?.taskStep || 2,
            startDate: currentTask?.startDate || new Date(),
            taskName: AsaTaskName.CAREERROADMAP,
          }),
        );
      }

      if (currsentAsaTask && (currsentAsaTask?.taskStep as number) != 2) {
        if (currsentAsaTask?.taskUrl) {
          const url = new URL(currsentAsaTask?.taskUrl || '');
          navigate(`${url.pathname}${url.search}`);
          // setIsDisabled(true);
        }
      }
    }
  }, [asaUserTaskHistory, currentUser, currsentAsaTask]);

  const regionalLocationState = useSelector(
    (state: RootState) => state.careerRoadmap.region_location,
  );
  const state_options = (Object.keys(StateValue) as Array<keyof typeof StateValue>)
    .filter((key) => typeof StateValue[key] === 'string')
    .map((key) => StateValue[key]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(CareerRoadMapSchema),
    defaultValues: {
      state: regionalLocationState?.answer || '',
      commute: regionalLocationState?.answer2 || '',
    },
  });

  const backButtonClick = () => {
    onBack();
  };

  const submitCareerRoadMapAnswer: SubmitHandler<FormData> = async (data: FormData) => {
    logger.debug('Submitted personal info.', data);
    setIsLoading(true);

    try {
      if (data) {
        const dto: CareerRoadMapDto = {
          id: roadMapQuestions[0].questionNumber, // make sure index matches!
          answer: data.state,
          question: roadMapQuestions[0].question,
          answer2: data.commute,
        };

        void dispatch(dispatchCareerRoadMapAssessment(dto, 'region_location'));

        // asa task
        if (currentUser?.claims?.isAsaUser && currsentAsaTask) {
          const endPointUrl = process.env[EnvironmentVariable.REACT_APP_ASA_COMPLETE_TASK_URL];
          const isStaging =
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('staging') ||
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('localhost');
          const taskResponse = await completeASATask(
            {
              asaUserId: currentUser?.claims?.asaUserId as number,
              taskId: currsentAsaTask?.taskId,
              taskStep: currsentAsaTask?.taskStep || step,
            },
            endPointUrl || '',
            isStaging || false,
          );

          if (!taskResponse || !taskResponse?.ok) {
            toast.error('Something went wrong!, Please try after sometime.');
            return;
          }

          let url = new URL(currsentAsaTask?.taskUrl || '');
          const nextTaskUrl = buildASANextTaskUrl(url, AsaTaskName.CAREERROADMAP, step + 1);
          url = new URL(nextTaskUrl || '');
          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              startDate: new Date(),
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep:
                (currsentAsaTask && parseInt(currsentAsaTask?.taskStep.toString()) + 1) || 2,
              taskUrl: nextTaskUrl,
              taskName: AsaTaskName.CAREERROADMAP,
              totalSteps: 17,
            }),
          );

          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep: currsentAsaTask?.taskStep || step,
              isCompleted: true,
              completedDate: new Date(),
            }),
          );
          toast.success('Task has been successfully submitted');
          navigate(`${url.pathname}${url.search}`);
        } else {
          navigate(`/career-roadmap/step${step + 1}`);
        }
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    reset({
      state: regionalLocationState?.answer,
      commute: regionalLocationState?.answer2,
    });
  }, [reset, regionalLocationState?.answer, regionalLocationState?.answer2]);

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <div>
      {isLoading && <CircularLoader />}

      <div className="flex mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">
          {roadMapQuestions[0].questionNumber}
        </div>
        <div>
          <h4 className="text-2xl">
            <span className="font-semibold">{roadMapQuestions[0].question} </span>
          </h4>
        </div>
      </div>
      <div className="character absolute bottom-0 right-[-85px] lg:right-[-138px]">
        <img className="" src={character} alt="" />
      </div>
      <div className="flex  mt-9 mb-10 md:px-10">
        <Box
          className="w-full"
          component="form"
          sx={{ '& > :not(style)': { mb: 4 } }}
          autoComplete="off"
          onSubmit={handleSubmit(submitCareerRoadMapAnswer)}
        >
          <div className="grid grid-cols-1 gap-5">
            <Controller
              control={control}
              name="state"
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  freeSolo
                  options={state_options}
                  value={value || ''}
                  onChange={(_, newValue) => onChange(newValue)}
                  onInputChange={(_, newInputValue) => onChange(newInputValue)}
                  renderInput={(params) => (
                    <TextField
                      className="w-full"
                      {...params}
                      label="State"
                      required
                      error={!!errors.state}
                      helperText={errors.state?.message}
                    />
                  )}
                />
              )}
            />

            <Controller
              control={control}
              name="commute"
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label="Commute radius / travel mode"
                  value={field.value}
                  onChange={field.onChange}
                  required
                  error={!!errors.commute}
                  helperText={errors.commute?.message}
                >
                  <MenuItem value="">Select</MenuItem>
                  {roadMapQuestions[0]?.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </div>

          <div className="flex justify-end lg:justify-between lg:mt-20 mt-10">
            <button onClick={handleSubmit(backButtonClick)} className="btn-internal" type="button">
              Back
            </button>
            <button className="btn-internal ml-5" disabled={isLoading} type="submit">
              Next
            </button>
          </div>
        </Box>
      </div>
    </div>
  );
};
